import React, { createContext, useState, useEffect } from 'react';
import { StoryData } from 'storyblok-js-client';
import { graphql, useStaticQuery } from 'gatsby';
import { TranslationService } from '@rocheglobal/component-library/src/services/translation/index';
import { GlobalConfigProps } from '../dom';

export const PageStateContext = createContext<IPageStateContext>(null);

export interface IPageStateContext {
  environment: GlobalConfigProps;
  [key: string]: unknown;
}

export interface PageContextProps {
  children: JSX.Element[] | JSX.Element;
  story: StoryData;
  resolvedLinks: Link[];
  cartLink?: string;
  environment?: GlobalConfigProps;
}

export type Settings = {
  press_articles_overview_link_label: string;
  press_articles_overview_link_url;
  press_articles_teaser_fallback_image;
  login_page_url;
  load_more_button_label: string;
  items_to_load: number;
}

export type Link = {
  uuid: string;
  slug: string;
}

/**
 * PageContextProvide is storing the content of the current story/page
 */
// eslint-disable-next-line max-len
export const PageContextProvider = ({
  story, resolvedLinks, children, cartLink, environment,
}: PageContextProps): JSX.Element => {
  const [page, setPage] = useState(story);

  // Keeping these for back reference in case needed
  // qContacts: ContactItems(by_slugs: "/^global-data/contacts/") {

  // List of products and their docs
  const { qDipps, storyblok } = useStaticQuery(graphql`
    {
      qDipps: allDipps {
        nodes {
          activeSubstance
          authorityName
          blackTriangle
          familyName
          id
          sapCode
          documents {
            sapCode
            downloadUrl
            eldra
            infoTypeCode
            strengthName
            strengthAbbrevation
          }
        }
      }
      storyblok {
        qSettings: SettingsItems(by_slugs: "global-data/settings", resolve_links: "url") {
          items {
            content {
              _editable
              _uid
              component
              items_to_load
              load_more_button_label
              protected_press_articles_text
              press_articles_overview_link_label
              press_articles_overview_link_url {
                url
                id
                fieldtype
                cachedUrl
                email
                linktype
                story {
                  slug
                  path
                  fullSlug
                }
              }
              press_articles_about_roche_label
              press_articles_about_roche_url {
                url
                id
                fieldtype
                cachedUrl
                email
                linktype
                story {
                  slug
                  path
                  fullSlug
                }
              }
              press_articles_teaser_fallback_image {
                alt
                copyright
                filename
                name
                title
                id
                focus
              }
              product_overview_teaser_fallback_image {
                alt
                copyright
                filename
                focus
                id
                name
                title
              }
              product_catalog_dropdown_category_placeholder
              product_catalog_dropdown_subcategory_placeholder
              did_overview_page_link {
                cachedUrl
                story {
                  slug
                  path
                  fullSlug
                }
              }
              blog_overview_page_link {
                cachedUrl
                story {
                  slug
                  path
                  fullSlug
                }
              }
              did_no_results_message
            } 
          }
        }   
        qContacts: ContactItems {
          items {
            content {
              firstname
              job_title
              lastname
              slotted_links
              slotted_media
              title
              component
              _editable
              _uid
            }
            uuid
            name
          }
        }
      }
    }
  `);

  // eslint-disable-next-line max-len
  const [settings, setSettings] = useState(storyblok.qSettings.items[0] && storyblok.qSettings.items[0].content ? storyblok.qSettings.items[0].content : null);
  const [products, setProducts] = useState(qDipps?.nodes);
  const [contacts, setContacts] = useState(storyblok.qContacts.items);
  const [pictureCreditsAdditionalData, setPictureCreditsAdditionalData] = useState('');
  const [translations, setTranslations] = useState({});
  const [links] = useState(resolvedLinks || []);

  useEffect(() => {
    const getTranslations = async (): Promise<void> => {
      setTranslations(await TranslationService.getTranslation());
    };
    getTranslations();
  }, []);

  return (
    <>
      <PageStateContext.Provider value={{
        page,
        setPage,
        products,
        setProducts,
        contacts,
        setContacts,
        settings,
        setSettings,
        pictureCreditsAdditionalData,
        setPictureCreditsAdditionalData,
        translations,
        links,
        cartLink,
        environment,
      }}>
        {children}
      </PageStateContext.Provider>
    </>
  );
};
